import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  Plus,
  Loader2,
  CheckCircle2,
  XCircle,
  Eye,
  Archive as ArchiveIcon,
  Trash2,
  X,
} from 'lucide-react';
// Note: the shadcn Button in this project does not support `asChild`, so we
// compose buttons that navigate via `useNavigate` instead of wrapping a Link.
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useAuthContext } from '@/contexts/auth-context';
import {
  useAdminContent,
  useAdminPendingContent,
  useBulkContentAction,
  useMyContent,
} from '@/hooks/use-content';
import {
  ContentStatus,
  type ContentItem,
  type ContentStatus as ContentStatusType,
  contentStatusLabels,
  contentTypeLabels,
  contentVisibilityLabels,
} from '@/types/content';
import { RejectReasonDialog } from '@/components/content/reject-reason-dialog';
import { toToastMessage } from '@/lib/api-error';

// SPEC-021: ContentListPage is the landing page for the Content Management
// section. Authors see "My content" by default; admins get extra "All
// content" and "Moderation queue" tabs, row selection, and bulk actions.

const PER_PAGE = 20;

type Tab = 'mine' | 'all' | 'pending';

export default function ContentListPage() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState<Tab>('mine');
  const [status, setStatus] = useState<ContentStatusType | ''>('');
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PER_PAGE;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectOpen, setRejectOpen] = useState(false);

  const mineQuery = useMyContent({
    status: tab === 'mine' ? status || undefined : undefined,
    limit: PER_PAGE,
    offset,
  });
  const adminQuery = useAdminContent({
    status: tab === 'all' ? status || undefined : undefined,
    limit: PER_PAGE,
    offset,
  });
  const pendingQuery = useAdminPendingContent({ limit: PER_PAGE, offset });
  const bulk = useBulkContentAction();

  const active =
    tab === 'pending' ? pendingQuery : tab === 'all' && isAdmin ? adminQuery : mineQuery;
  const items = active.data?.data ?? [];
  const pagination = active.data?.pagination;

  // Clear selection whenever the user switches tabs, changes filter, or
  // navigates to a different page. Keeping stale ids across views is a
  // footgun — an admin flipping from "published" to "pending" and
  // clicking "reject" would reject whatever was selected on the other tab.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab, status, page]);

  const adminSelectable = isAdmin && (tab === 'all' || tab === 'pending');
  const allOnPageSelected = useMemo(
    () => items.length > 0 && items.every((i) => selectedIds.has(i.id)),
    [items, selectedIds],
  );

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        items.forEach((i) => next.delete(i.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        items.forEach((i) => next.add(i.id));
        return next;
      });
    }
  };

  const runBulk = async (
    action: 'approve' | 'reject' | 'unpublish' | 'archive',
    reason?: string,
  ) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const result = await bulk.mutateAsync({ ids, action, reason });
      const failures = Object.keys(result.failures ?? {}).length;
      if (failures === 0) {
        toast.success(`${action} applied to ${result.success} item(s)`);
      } else {
        toast.warning(
          `${action}: ${result.success} succeeded, ${failures} failed`,
        );
      }
      setSelectedIds(new Set());
      setRejectOpen(false);
    } catch (e) {
      toast.error(toToastMessage(e, `Bulk ${action} failed`));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Learning Hub</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/content/trash')}>
              <Trash2 className="mr-2 h-4 w-4" />
              Trash
            </Button>
          )}
          <Button onClick={() => navigate('/content/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New content
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <TabButton active={tab === 'mine'} onClick={() => switchTab('mine', setTab, setPage)}>
          My content
        </TabButton>
        {isAdmin && (
          <>
            <TabButton active={tab === 'all'} onClick={() => switchTab('all', setTab, setPage)}>
              All content
            </TabButton>
            <TabButton
              active={tab === 'pending'}
              onClick={() => switchTab('pending', setTab, setPage)}
            >
              Moderation queue
            </TabButton>
          </>
        )}

        {tab !== 'pending' && (
          <div className="ml-auto">
            <Select
              value={status === '' ? 'all' : status}
              onValueChange={(v) => {
                setStatus(v === 'all' ? '' : (v as ContentStatusType));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(Object.keys(contentStatusLabels) as ContentStatusType[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {contentStatusLabels[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {adminSelectable && selectedIds.size > 0 && (
        <BulkActionsToolbar
          count={selectedIds.size}
          busy={bulk.isPending}
          onApprove={() => runBulk('approve')}
          onRejectOpen={() => setRejectOpen(true)}
          onUnpublish={() => runBulk('unpublish')}
          onArchive={() => runBulk('archive')}
          onClear={() => setSelectedIds(new Set())}
        />
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {tab === 'pending'
              ? 'Items awaiting review'
              : tab === 'all'
                ? 'All content items'
                : 'My content items'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {active.isLoading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading content…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No content items yet.{' '}
              <Link to="/content/new" className="text-primary hover:underline">
                Create your first piece
              </Link>
              .
            </div>
          ) : (
            <ContentTable
              items={items}
              selectable={adminSelectable}
              selectedIds={selectedIds}
              allOnPageSelected={allOnPageSelected}
              onToggleRow={toggleRow}
              onToggleAllOnPage={toggleAllOnPage}
              onOpen={(id) => navigate(`/content/${id}`)}
            />
          )}
        </CardContent>
      </Card>

      {pagination && pagination.total_pages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  setPage((p) => Math.max(1, p - 1));
                }}
                aria-disabled={page === 1}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink isActive>{page}</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <span className="px-2 text-xs text-muted-foreground">
                of {pagination.total_pages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  setPage((p) => Math.min(pagination.total_pages, p + 1));
                }}
                aria-disabled={page >= pagination.total_pages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <RejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title={`Reject ${selectedIds.size} item(s)`}
        busy={bulk.isPending}
        onSubmit={(reason) => runBulk('reject', reason)}
      />
    </div>
  );
}

function switchTab(
  next: Tab,
  setTab: (t: Tab) => void,
  setPage: (p: number) => void,
) {
  setTab(next);
  setPage(1);
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

interface BulkActionsToolbarProps {
  count: number;
  busy: boolean;
  onApprove: () => void;
  onRejectOpen: () => void;
  onUnpublish: () => void;
  onArchive: () => void;
  onClear: () => void;
}

function BulkActionsToolbar({
  count,
  busy,
  onApprove,
  onRejectOpen,
  onUnpublish,
  onArchive,
  onClear,
}: BulkActionsToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
      <span className="font-medium">{count} selected</span>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onApprove} disabled={busy}>
          <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
        </Button>
        <Button size="sm" variant="destructive" onClick={onRejectOpen} disabled={busy}>
          <XCircle className="mr-1 h-4 w-4" /> Reject
        </Button>
        <Button size="sm" variant="outline" onClick={onUnpublish} disabled={busy}>
          <Eye className="mr-1 h-4 w-4" /> Unpublish
        </Button>
        <Button size="sm" variant="outline" onClick={onArchive} disabled={busy}>
          <ArchiveIcon className="mr-1 h-4 w-4" /> Archive
        </Button>
        <Button size="icon" variant="ghost" onClick={onClear} disabled={busy}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface ContentTableProps {
  items: ContentItem[];
  selectable: boolean;
  selectedIds: Set<string>;
  allOnPageSelected: boolean;
  onOpen: (id: string) => void;
  onToggleRow: (id: string) => void;
  onToggleAllOnPage: () => void;
}

function ContentTable({
  items,
  selectable,
  selectedIds,
  allOnPageSelected,
  onOpen,
  onToggleRow,
  onToggleAllOnPage,
}: ContentTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {selectable && (
            <TableHead className="w-10">
              <Checkbox
                checked={allOnPageSelected}
                onCheckedChange={onToggleAllOnPage}
                aria-label="Select all on page"
              />
            </TableHead>
          )}
          <TableHead>Title</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Visibility</TableHead>
          <TableHead>Minutes</TableHead>
          <TableHead>Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow
            key={item.id}
            className="cursor-pointer"
            onClick={() => onOpen(item.id)}
          >
            {selectable && (
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds.has(item.id)}
                  onCheckedChange={() => onToggleRow(item.id)}
                  aria-label={`Select ${item.title}`}
                />
              </TableCell>
            )}
            <TableCell className="max-w-[320px]">
              <p className="truncate font-medium">{item.title}</p>
              {item.summary && (
                <p className="truncate text-xs text-muted-foreground">{item.summary}</p>
              )}
            </TableCell>
            <TableCell>
              <Badge variant="outline">{contentTypeLabels[item.content_type]}</Badge>
            </TableCell>
            <TableCell>
              <StatusBadge status={item.status} />
            </TableCell>
            <TableCell className="text-xs">
              {contentVisibilityLabels[item.visibility]}
            </TableCell>
            <TableCell>{item.estimated_minutes ?? '—'}</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {new Date(item.updated_at).toLocaleDateString()}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function StatusBadge({ status }: { status: ContentStatusType }) {
  const variant: Record<ContentStatusType, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    draft: 'outline',
    pending_review: 'secondary',
    published: 'default',
    rejected: 'destructive',
    archived: 'outline',
  };
  return (
    <Badge variant={variant[status]}>
      {status === ContentStatus.PendingReview ? 'Pending' : contentStatusLabels[status]}
    </Badge>
  );
}
